import React, { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Plus, TrashIcon, ArrowLeft, AlertTriangle, RefreshCw, Printer } from "lucide-react"
import moment from "moment"

import { useToast } from "~/utils/Toast"
import { formatCurrency } from "~/utils/helpers"
import buildBookingPaymentItems from "~/utils/buildBookingPaymentItems"

import Booking from "~/types/Booking"
import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import CatchTable from "~/library/CatchTable"
import ResourceNotes from "~/library/ResourceNotes"
import ConfirmationModal from "~/library/ConfirmationModal"
import NavigationLink from "~/library/NavigationLink"

import type { PayablePaymentResource } from "~/types/PayablePaymentResource"
import type PaymentItem from "~/types/PaymentItem"
import type Pack from "~/types/Pack"
import type PackItem from "~/types/PackItem"
import type BookingItem from "~/types/BookingItem"
import type { BookingItemAttribute, BookingItemChange } from "~/types/BookingItemPayload"

import LineItemModal from "./LineItemModal"
import BookingPrintReceipt from "./BookingPrintReceipt"
import BookingStatusManager from "~/components/BookingStatusManager"
import PayablePaymentsSection from "~/components/payments/PayablePaymentsSection"
import { serializeModifiers } from "./lineItemModifierHelpers"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiBookings } from "~/routes"
import {
  getBookingsBackPath,
  getBookingsBackTitle,
  getBookingsNavigationState,
} from "~/utils/navigationState"

const emptyMoney = { cents: 0, currency_iso: "USD" }

const BookingView = () => {
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false)
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
  const [showRecalculateTotalsModal, setShowRecalculateTotalsModal] = useState(false)
  const [isRecalculatingTotals, setIsRecalculatingTotals] = useState(false)
  const [isPrintReceiptOpen, setIsPrintReceiptOpen] = useState(false)

  const { venueSlug, stripePublishableKey } = getAppConfig()
  const navigate = useNavigate()
  const { id } = useParams()

  const bookingId = id

  const endpoint = apiBookings.show.path({ venueSlug, id: bookingId })
  const {
    data: booking,
    refetch,
    isLoading,
  } = useFetchResource<{ booking: Booking }, "booking">(endpoint, "booking", {})

  const toast = useToast()

  const ensureBookingId = () => {
    if (!bookingId) {
      toast("Booking id is missing", "danger")
      return null
    }

    return bookingId
  }

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const toggleBookingArchived = () => {
    const currentBookingId = ensureBookingId()
    if (!currentBookingId) return

    const req = booking?.archivedAt
      ? apiBookings.unarchive({ venueSlug, id: currentBookingId })
      : apiBookings.archive({ venueSlug, id: currentBookingId })

    req.then(refetch).catch((res) => {
      toast(res.body?.errors?.join(", ") || "Failed to update booking archive status", "danger")
    })
  }

  const handleSaveBookingItems = (changes: BookingItemChange[]) => {
    const currentBookingId = ensureBookingId()
    if (!currentBookingId) return

    const bookingItemsAttributes: BookingItemAttribute[] = changes.map((change) => ({
      id: change.id,
      line_item_id: change.lineItemId,
      name: change.name,
      category_name: change.categoryName,
      quantity: change.quantity,
      amount_cents: change.amountCents,
      modifiers: serializeModifiers(change.modifierIds),
      _destroy: change.action === "remove" ? true : undefined,
    }))

    apiBookings
      .update({
        data: {
          venue_slug: venueSlug,
          id: currentBookingId,
          booking: {
            booking_items_attributes: bookingItemsAttributes,
          },
        },
      })
      .then(() => {
        toast(`Successfully updated ${changes.length} booking items`, "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to update booking items", "danger")
      })
  }

  const handleRemoveBookingItem = (bookingItemId: string) => {
    const currentBookingId = ensureBookingId()
    if (!currentBookingId) return

    apiBookings
      .update({
        data: {
          venue_slug: venueSlug,
          id: currentBookingId,
          booking: {
            booking_items_attributes: [{ id: bookingItemId, _destroy: true }],
          },
        },
      })
      .then(() => {
        toast("Booking item removed", "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to remove booking item", "danger")
      })
  }

  const handleStatusChangeToConfirmed = () => {
    const currentBookingId = ensureBookingId()
    if (!currentBookingId) return

    apiBookings
      .update({
        data: {
          venue_slug: venueSlug,
          id: currentBookingId,
          booking: { status: "pending" },
        },
      })
      .then(() => {
        toast("Booking status updated.", "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to update booking status", "danger")
      })
      .finally(() => {
        setShowStatusChangeModal(false)
      })
  }

  const handleRecalculateTotals = () => {
    const currentBookingId = ensureBookingId()
    if (!currentBookingId) return

    setIsRecalculatingTotals(true)

    apiBookings
      .recalculateTotals({ venueSlug, id: currentBookingId })
      .then(() => {
        toast("Booking totals recalculated successfully.", "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to recalculate booking totals", "danger")
      })
      .finally(() => {
        setIsRecalculatingTotals(false)
        setShowRecalculateTotalsModal(false)
      })
  }

  const formatStatus = (status?: string) => {
    if (status === "pending") return <span className="badge badge-lg bg-warning">Pending</span>
    if (status === "confirmed") return <span className="badge badge-lg bg-success">Confirmed</span>
    return <span className="badge badge-lg bg-secondary">{status}</span>
  }

  const getBackNavigation = () => {
    const navigationState = getBookingsNavigationState()

    if (
      navigationState?.path &&
      (navigationState.path.includes("/clients/") || navigationState.path.includes("/areas/"))
    ) {
      return {
        path: navigationState.path,
        title: navigationState.title,
      }
    }

    return {
      path: getBookingsBackPath(),
      title: getBookingsBackTitle(),
    }
  }

  const backNavigation = getBackNavigation()

  const paymentResource: PayablePaymentResource | null =
    booking && bookingId
      ? {
          payableType: "booking",
          payableId: bookingId,
          customer: booking.client
            ? {
                name: booking.client.name,
                email: booking.client.email,
                phone: booking.client.phone,
              }
            : null,
          currency: booking.totalAmount?.currency_iso || "USD",
          summary: {
            title: booking.name,
            subtitle: moment(booking.date).format("MMMM Do, YYYY"),
            meta: booking.area?.name,
            details: booking.details,
          },
        }
      : null

  const initialPaymentItems: PaymentItem[] = useMemo(
    () => buildBookingPaymentItems(booking),
    [booking]
  )

  const bookingPayments = useMemo(() => booking?.payments || [], [booking])
  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(backNavigation.path)}
              aria-label={`Go back to ${backNavigation.title}`}
              title={`Go back to ${backNavigation.title}`}
            >
              <ArrowLeft />
            </button>
            <span>{`${booking?.name}${booking?.archivedAt ? " (Archived)" : ""}`}</span>
          </div>
        }
      >
        <>
          <BookingStatusManager booking={booking} onUpdate={refetch} />
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => setIsPrintReceiptOpen(true)}
            title="Print Booking"
          >
            <Printer size={16} className="me-2" />
            Print
          </button>
          <CatchDropdown
            label="Actions"
            className="ms-2"
            items={[
              {
                label: "Edit Booking",
                to: `/bookings/${bookingId}/edit`,
              },
              ...(booking?.status === "confirmed"
                ? [{ label: "Change Status", onClick: () => setShowStatusChangeModal(true) }]
                : []),
              {
                label: booking?.archivedAt ? "Unarchive" : "Archive",
                onClick: toggleBookingArchived,
              },
            ]}
          />
        </>
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Booking Details">
            <div className="row">
              {renderDetail("Booking Name", booking?.name, 4)}
              {renderDetail(
                "Booked Area",
                <NavigationLink type="areas" to={`/areas/${booking?.area?.id}`}>
                  {booking?.area?.name}
                </NavigationLink>,
                4
              )}
              {renderDetail("Date", moment(booking?.date).format("MMM Do YYYY"), 4)}
              <div className="row mt-2">
                {renderDetail("Additional Details", booking?.details || <em>No Details</em>, 4)}
                {renderDetail("Current Status", formatStatus(booking?.status), 4)}
              </div>
            </div>
          </CatchCard>

          <CatchCard title="Client Details">
            <div className="row">
              {renderDetail(
                "Name",
                <NavigationLink type="clients" to={`/clients/${booking?.client?.id}`}>
                  {booking?.client?.name}
                </NavigationLink>,
                4
              )}
              {renderDetail("Phone", booking?.client?.phone, 4)}
              {renderDetail("Email", booking?.client?.email, 4)}
            </div>
          </CatchCard>

          <CatchCard>
            <div className="d-flex align-items-middle mb-2">
              <h4>Selected Packs</h4>
            </div>

            <div className="row">
              {!booking?.packs || booking.packs.length === 0 ? (
                <div className="text-center p-3 text-muted">
                  No packs selected for this booking.
                </div>
              ) : (
                <CatchTable
                  data={booking.packs}
                  emptyMessage="No packs selected for this booking"
                  columns={[
                    {
                      key: "name",
                      header: "Pack Name",
                      formatter: (name: string, data: Pack) => (
                        <NavigationLink type="packs" to={`/packs/${data.prefixId || data.id}`}>
                          {name}
                        </NavigationLink>
                      ),
                    },
                    {
                      key: "active",
                      header: "Status",
                      formatter: (active: boolean) => (active ? "Active" : "Inactive"),
                    },
                    {
                      key: "price",
                      header: "Price",
                      formatter: (price: { cents: number; currency_iso: string }) =>
                        formatCurrency(price || emptyMoney),
                    },
                    {
                      key: "items",
                      header: "Items Included",
                      formatter: (items: PackItem[]) =>
                        items?.length > 0 ? (
                          <small className="text-muted">
                            {items
                              .map((item: PackItem) => `${item.lineItem?.name} (${item.quantity})`)
                              .join(", ")}
                          </small>
                        ) : (
                          <small className="text-muted">No items</small>
                        ),
                    },
                  ]}
                  footerData={{
                    className: "table table-centered",
                    rowData: [
                      { cellData: [{ content: "" }] },
                      {
                        className: "bg-block",
                        cellData: [
                          { content: "Total", colSpan: 2 },
                          {
                            content: formatCurrency(booking?.packsTotal || emptyMoney),
                            colSpan: 2,
                          },
                        ],
                      },
                    ],
                  }}
                />
              )}
            </div>
          </CatchCard>

          <CatchCard>
            <div className="d-flex align-items-center mb-2">
              <h4>Line Items</h4>
              <button
                className="btn btn-primary btn-sm ms-auto"
                onClick={() => setIsLineItemModalOpen(true)}
              >
                <Plus className="me-2" />
                Add Line Item
              </button>
            </div>

            <div className="row">
              {!booking?.bookingItems || booking.bookingItems.length === 0 ? (
                <div className="text-center p-3 text-muted">No booking items added yet.</div>
              ) : (
                <CatchTable
                  data={booking.bookingItems}
                  rowKey={(bookingItem: BookingItem) => bookingItem.id || bookingItem.lineItemId}
                  subRowClassName="booking-line-item__sub-row"
                  emptyMessage="No Booking Items"
                  columns={[
                    {
                      key: "name",
                      header: "Title",
                      formatter: (name: string, data: BookingItem) => (
                        <NavigationLink type="line-items" to={`/line-items/${data.lineItem.id}`}>
                          {name}
                        </NavigationLink>
                      ),
                    },
                    {
                      key: "categoryName",
                      header: "Category",
                      formatter: (category: string) => category || "—",
                    },
                    {
                      key: "quantity",
                      header: "Quantity",
                      formatter: (quantity: number) => quantity,
                    },
                    {
                      key: "amount",
                      header: "Price",
                      formatter: (amount: { cents: number; currency_iso: string }) =>
                        formatCurrency(amount),
                    },
                    {
                      key: "subtotal",
                      header: "Subtotal",
                      formatter: (subtotal: { cents: number; currency_iso: string }) =>
                        formatCurrency(subtotal),
                    },
                    {
                      key: "modifierDetails",
                      header: "Modifiers",
                      formatter: (modifierDetails: BookingItem["modifierDetails"]) =>
                        modifierDetails?.length
                          ? modifierDetails
                              .map((modifier) =>
                                modifier.quantity > 1
                                  ? `${modifier.name}(${modifier.quantity})`
                                  : modifier.name
                              )
                              .join(", ")
                          : "",
                    },
                    {
                      key: "id",
                      header: "",
                      formatter: (_: unknown, data: BookingItem) => (
                        <TrashIcon
                          className="text-danger cursor-pointer"
                          onClick={() => data.id && handleRemoveBookingItem(data.id)}
                        />
                      ),
                    },
                  ]}
                  footerData={{
                    className: "table table-centered",
                    rowData: [
                      { cellData: [{ content: "" }] },
                      {
                        className: "bg-block",
                        cellData: [
                          { content: "Total", colSpan: 4 },
                          {
                            content: formatCurrency(booking?.lineItemsTotal || emptyMoney),
                            colSpan: 5,
                          },
                        ],
                      },
                    ],
                  }}
                />
              )}
            </div>
          </CatchCard>

          {paymentResource && (
            <div className="mt-3">
              <PayablePaymentsSection
                resource={paymentResource}
                payments={bookingPayments}
                initialPaymentItems={initialPaymentItems}
                paymentOwner={
                  booking?.client
                    ? {
                        name: booking.client.name,
                        email: booking.client.email,
                        phone: booking.client.phone,
                      }
                    : null
                }
                stripePublishableKey={stripePublishableKey}
                onRefresh={refetch}
                totalAmountCents={booking?.totalAmountCents || 0}
              />
            </div>
          )}
        </div>

        <div className="col-4">
          <CatchCard title="Financials">
            <div className="row">
              {booking?.pricingOutdated && (
                <div className="col-12 mb-3">
                  <div className="alert alert-warning d-flex align-items-start">
                    <AlertTriangle className="me-2 mt-1" size={18} />
                    <div className="w-100">
                      <div className="fw-semibold mb-1">Totals are outdated</div>
                      <div className="small mb-2">
                        Venue tax or surcharge settings changed after this booking was calculated.
                      </div>

                      <button
                        type="button"
                        className="btn bg-warning btn-sm"
                        onClick={() => setShowRecalculateTotalsModal(true)}
                      >
                        <RefreshCw className="me-2" size={14} />
                        Recalculate Totals
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {renderDetail(
                "Manual Subtotal",
                formatCurrency(booking?.anticipatedRevenue || emptyMoney),
                12
              )}
              {renderDetail("Subtotal", formatCurrency(booking?.subtotalAmount || emptyMoney), 12)}

              {(booking?.bookingSurchargeAmountCents || 0) > 0 &&
                renderDetail(
                  `Booking Surcharge (${booking?.bookingSurchargeRate || 0}%)`,
                  formatCurrency(booking?.bookingSurchargeAmount || emptyMoney),
                  12
                )}

              {(booking?.taxAmountCents || 0) > 0 &&
                renderDetail(
                  `Tax (${booking?.taxRate || 0}%)`,
                  formatCurrency(booking?.taxAmount || emptyMoney),
                  12
                )}

              {renderDetail("Total", formatCurrency(booking?.totalAmount || emptyMoney), 12)}
            </div>
          </CatchCard>

          <CatchCard title="Internal Details">
            <div className="row">
              {booking?.archivedAt &&
                renderDetail("Archived", moment(booking.archivedAt).fromNow(), 12)}
              {renderDetail("Created by", booking?.createdBy?.email, 12)}
              {renderDetail(
                "Created on",
                moment(booking?.createdAt).format("MMM Do YYYY hh:mma"),
                12
              )}
            </div>
          </CatchCard>

          {bookingId && (
            <CatchCard title="Notes">
              <ResourceNotes resourceType="booking" resourceId={bookingId} />
            </CatchCard>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showStatusChangeModal}
        onClose={() => setShowStatusChangeModal(false)}
        onConfirm={handleStatusChangeToConfirmed}
        title="Confirm Status Change"
      >
        <p>
          Are you sure you want to mark this booking as <strong>Pending</strong>?
        </p>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={showRecalculateTotalsModal}
        onClose={() => setShowRecalculateTotalsModal(false)}
        onConfirm={handleRecalculateTotals}
        title="Recalculate Booking Totals"
        confirmText={isRecalculatingTotals ? "Recalculating..." : "Recalculate"}
        confirmDisabled={isRecalculatingTotals}
      >
        <p>
          This will recalculate surcharge, tax, and total using the current venue pricing settings.
        </p>
        <p className="mt-2 mb-0">
          Existing snapshot totals on this booking will be replaced, even if payments already exist.
        </p>
      </ConfirmationModal>

      {isLineItemModalOpen && (
        <LineItemModal
          isOpen={isLineItemModalOpen}
          onClose={() => setIsLineItemModalOpen(false)}
          onSave={handleSaveBookingItems}
          existingBookingItems={booking?.bookingItems || []}
        />
      )}

      {isPrintReceiptOpen && booking && (
        <BookingPrintReceipt booking={booking} onClose={() => setIsPrintReceiptOpen(false)} />
      )}
    </LoadingWrapper>
  )
}

export default BookingView
